/**
 * Générateur — Automatisme : calculer un effectif manquant dans un tableau
 * pour un petit nombre de valeurs.
 * Attendu ID : 1126 | Niveau : 4ème | Domaine : Statistiques
 * Rubrique : AUTOMATISME (programme 2026, Statistiques 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - L'effectif manquant            (saisie ; QCM à leurres calculés)
 *   - Quel calcul ?                  (QCM : l'expression qui donne l'effectif manquant, ou le total)
 *   - Tableau à double entrée        (saisie : une case manquante, marges connues)
 *   - Le tableau, vrai ou faux ?     (V/F sur un tableau à case manquante)
 *   - Effectifs et total : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-07) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Statistiques 4e, automatisme « Calculer un
 * effectif manquant dans un tableau pour un petit nombre de valeurs » + grille
 * P1-P12. P1 : CM2 introduit (lire un tableau simple ou à double entrée,
 * problème en 1-2 étapes), 6e consolidé (lecture immédiate, construire un
 * tableau simple), 5e formalisé (calculer des effectifs et des fréquences),
 * 4e automatisme. P2 : sens installé — le tableau d'effectifs comme
 * parties-tout, le total est la somme des effectifs ; non installés
 * (distracteurs) — la fréquence (autre automatisme 4e), la confusion valeur /
 * effectif. P3 : effectifs entiers, 3 à 5 catégories, total ≤ 60. P4 :
 * parties-tout, l'énoncé n'induit pas l'opération. P5 : tableau simple,
 * tableau à double entrée (CM2). P6 : aucune calculatrice. P7 : pas de
 * fréquence ni de pourcentage. P8 : effectif, effectif total, catégorie.
 * P9 : additions/soustractions d'entiers et « une addition à trou se complète
 * par une soustraction » (automatisme 5e) restent implicites. P10 : constat.
 * P11 : prendre le total pour l'effectif manquant ; ne retirer qu'une
 * catégorie ; additionner quand le total manque ; lire une valeur comme un
 * effectif. P12 : rémanence forte (tableaux depuis le CM1).
 * INVARIANTS :
 *   - le tableau est CONSTRUIT depuis (catégories, effectifs, total) et rendu
 *     en HTML dans l'énoncé ; la case manquante porte « ? » ;
 *   - effectifs ≥ 1, total ≤ 60, somme des connus puis soustraction de tête ;
 *   - leurres de QCM calculés et vérifiés distincts ; V/F : pas de crochets,
 *     mixité 1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
    retirerCodesErreur,
} from '../../../utils';
import { tableEnonce, ligneTable, ligneTableEntete } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1126;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_MANQUANT = 'L\'effectif manquant';
const L_CALCUL = 'Quel calcul ?';
const L_DOUBLE = 'Tableau à double entrée';
const L_VF = 'Le tableau, vrai ou faux ?';
const L_RAPPEL = 'Effectifs et total : vrai ou faux ?';

const MANQUE = '<span style="color:#B45309;font-weight:700">?</span>';

// ─── Contextes : une question posée à un groupe, 3 à 5 catégories ──────────
const CONTEXTES = [
    { id: 'sport', intro: (g) => `On a demandé aux ${g} leur sport préféré.`, entete: 'Sport', cats: ['Football', 'Basket', 'Natation', 'Tennis', 'Danse'] },
    { id: 'transport', intro: (g) => `On a demandé aux ${g} comment ils viennent au collège.`, entete: 'Transport', cats: ['À pied', 'Vélo', 'Bus', 'Voiture', 'Trottinette'] },
    { id: 'animal', intro: (g) => `On a demandé aux ${g} leur animal préféré.`, entete: 'Animal', cats: ['Chien', 'Chat', 'Poisson', 'Lapin', 'Oiseau'] },
    { id: 'couleur', intro: (g) => `On a demandé aux ${g} leur couleur préférée.`, entete: 'Couleur', cats: ['Bleu', 'Rouge', 'Vert', 'Jaune', 'Noir'] },
    { id: 'saison', intro: (g) => `On a demandé aux ${g} leur saison préférée.`, entete: 'Saison', cats: ['Printemps', 'Été', 'Automne', 'Hiver'] },
    { id: 'langue', intro: (g) => `On a demandé aux ${g} la langue qu'ils étudient.`, entete: 'Langue', cats: ['Anglais', 'Espagnol', 'Allemand', 'Italien'] },
];
const GROUPES = ['élèves d\'une classe de 4e', 'élèves d\'un club', 'membres d\'une association', 'enfants d\'un centre de loisirs', 'élèves de deux classes'];

/** Un tableau : k catégories (3 à 5), effectifs ≥ 1 distincts deux à deux quand possible, total ≤ 60. */
const tirerTableau = () => {
    for (let essai = 0; essai < 200; essai++) {
        const ctx = randomChoice(CONTEXTES);
        const k = randInt(3, Math.min(5, ctx.cats.length));
        const cats = shuffle(ctx.cats).slice(0, k);
        const eff = cats.map(() => randInt(2, 18));
        if (new Set(eff).size !== k) continue;
        const total = eff.reduce((a, b) => a + b, 0);
        if (total > 60 || total < 15) continue;
        return { ctx, cats, eff, total, groupe: randomChoice(GROUPES) };
    }
    throw new Error('gen_1126 : tableau introuvable');
};

const htmlTableau = (T, manquant /* index de catégorie, 'total' ou null */) => {
    const cellules = T.eff.map((e, i) => (manquant === i ? MANQUE : String(e)));
    const tot = manquant === 'total' ? MANQUE : String(T.total);
    return tableEnonce(
        ligneTableEntete([T.ctx.entete, ...T.cats, 'Total']),
        ligneTable('Effectif', [...cellules, tot]),
    );
};

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refS = { v: null }; const refQ = { v: null }; const refC = { v: null }; const refD = { v: null };
const signature = (T, extra) => `${T.ctx.id}-${T.cats.join('|')}-${T.eff.join('x')}-${extra}`;

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Valeurs fausses nommées de l'effectif manquant i : le total, la somme des connus, le total moins un seul connu.
const erreursManquant = (T, i) => {
    const connus = T.eff.filter((_, j) => j !== i);
    return [['total_pris_pour_le_maximum', T.total], ['somme_connus', connus.reduce((a, b) => a + b, 0)], ...connus.map((c) => ['moins_un_seul', T.total - c])];
};
/** Les erreurs sûres : valeurs positives, ni la bonne réponse, ni portées par deux codes. */
const erreursSures = (paires, juste) => {
    const parValeur = [];
    paires.forEach(([code, v]) => {
        if (v <= 0 || v === juste) return;
        const deja = parValeur.find((p) => p.v === v);
        if (deja) deja.codes.add(code); else parValeur.push({ v, codes: new Set([code]) });
    });
    return parValeur.filter((p) => p.codes.size === 1).map((p) => ({ code: [...p.codes][0], v: p.v }));
};

// ─── Carte 1a : l'effectif manquant (saisie) ───────────────────────────────

const construireManquantSaisie = () => {
    const T = tirerTableau(); const i = randInt(0, T.cats.length - 1);
    const texte = `<p>${T.ctx.intro(T.groupe)} Voici les résultats, mais un effectif a été effacé.</p>${htmlTableau(T, i)}<p>Quel est l'effectif de la catégorie « ${T.cats[i]} » ?</p>`;
    const payload = makeSaisie(String(T.eff[i]), texte, opts(L_MANQUANT));
    payload.contenu = { ...payload.contenu, sousType: `k${T.cats.length}` };
    payload._meta.params = { ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i, signature: signature(T, i) };
    const erreurs = erreursSures(erreursManquant(T, i), T.eff[i]).map((e) => ({ code: e.code, valeur: String(e.v) }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genManquantSaisie = () => antiRepeat(construireManquantSaisie, refS);

// ─── Carte 1b : l'effectif manquant (QCM à leurres calculés) ──────────────

const construireManquantQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const T = tirerTableau(); const i = randInt(0, T.cats.length - 1);
        const m = T.eff[i]; const connus = T.eff.filter((_, j) => j !== i); const S = connus.reduce((a, b) => a + b, 0);
        // Revue 2026-09-07 (A2) : les leurres « au-dessus » (total, somme, total − une seule) laissaient la bonne
        // réponse plus petite option dans 59 % des tirages. On tire le nombre de leurres SOUS la bonne réponse
        // uniformément entre 0 et 3 : la bonne est la plus petite ou la plus grande option un quart du temps chacune.
        const dessus = [
            { v: T.total, role: 'total' },                                       // le total pris pour l'effectif
            { v: S, role: 'somme_connus' },                                       // on additionne au lieu de soustraire
            { v: T.total - randomChoice(connus), role: 'moins_un_seul' },         // on ne retire qu'une catégorie
            { v: m + 1, role: 'voisin_plus' },                                    // erreur d'une unité
            { v: m + 10, role: 'dizaine_plus' },                                  // erreur de retenue
        ];
        const dessous = [
            { v: m - 1, role: 'voisin_moins' },
            { v: m - 10, role: 'dizaine_moins' },                                 // erreur de retenue (43 − 27 lu 16 au lieu de 26 ou l'inverse)
            ...connus.filter((c) => c < m).map((c) => ({ v: c, role: 'case_visible' })),   // recopie d'une case du tableau
        ];
        const valide = (c, pris) => c.v !== m && c.v > 0 && c.v <= T.total && !pris.some((l) => l.v === c.v);
        const nBas = randInt(0, 3);
        const leurres = [];
        shuffle(dessous).forEach((c) => { if (leurres.length < nBas && valide(c, leurres)) leurres.push(c); });
        shuffle(dessus).forEach((c) => { if (leurres.length < 3 && valide(c, leurres)) leurres.push(c); });
        if (leurres.length < 3) continue;
        const options = shuffle([m, ...leurres.map((l) => l.v)].map(String));
        const texte = `<p>${T.ctx.intro(T.groupe)} Voici les résultats, mais un effectif a été effacé.</p>${htmlTableau(T, i)}<p>Quel est l'effectif de la catégorie « ${T.cats[i]} » ?</p>`;
        const payload = makeQcm(options, String(m), texte, opts(L_MANQUANT));
        payload.contenu = { ...payload.contenu, sousType: `k${T.cats.length}` };
        payload._meta.params = { ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i, roles: leurres.map((l) => l.role), signature: signature(T, i) };
        const erreurs = erreursSures(erreursManquant(T, i), m).map((e) => ({ code: e.code, valeur: String(e.v) })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1126 : QCM manquant sans leurres');
};
const genManquantQcm = () => antiRepeat(construireManquantQcm, refQ);

// ─── Carte 2 : quel calcul ? (QCM sur l'expression) ────────────────────────
// Revue 2026-09-07 : la carte « Le total manquant » (saisie) est fusionnée ici. Deux cas à parts égales :
// une catégorie effacée (soustraction) ou le Total effacé (addition) — l'élève repère d'abord quelle case
// manque, l'énoncé n'induit pas l'opération.

const construireCalculQcm = () => {
    const T = tirerTableau(); const cas = randomChoice(['manquant', 'total']);
    let bonne; let leurres; let texte; let i = null;
    if (cas === 'manquant') {
        i = randInt(0, T.cats.length - 1);
        // Revue 2026-09-07 (A1) : si la case effacée vaut la somme des autres, le leurre « a + b » donne la bonne valeur.
        if (T.eff[i] * 2 === T.total) return construireCalculQcm();
        const connus = T.eff.filter((_, j) => j !== i); const somme = connus.join(' + ');
        bonne = `${T.total} − (${somme})`;
        // Revue 2026-09-07 (A2) : la soustraction à l'envers est TOUJOURS là (même longueur, mêmes parenthèses
        // que la bonne) ; les deux autres leurres, plus courts, sont tirés.
        leurres = [
            `(${somme}) − ${T.total}`,                                 // soustraction à l'envers
            ...shuffle([
                `${T.total} + ${somme}`,                               // on additionne tout
                `${somme}`,                                            // on oublie le total
                `${T.total} − ${randomChoice(connus)}`,                // on ne retire qu'une catégorie
            ]).slice(0, 2),
        ];
        texte = `<p>${T.ctx.intro(T.groupe)} Voici les résultats, mais un effectif a été effacé.</p>${htmlTableau(T, i)}<p>Quel calcul donne l'effectif de la catégorie « ${T.cats[i]} » ?</p>`;
    } else {
        const tous = T.eff.join(' + '); const mx = Math.max(...T.eff); const mn = Math.min(...T.eff);
        const oubli = randInt(0, T.eff.length - 1);
        const sans = T.eff.filter((_, j) => j !== oubli).join(' + ');
        bonne = tous;
        // Revue 2026-09-07 (A2) : un leurre aussi long que la somme complète est TOUJOURS là.
        const longs = [
            Array(T.eff.length).fill(mx).join(' + '),                  // on répète le plus grand effectif
            `${T.eff.slice(0, -1).join(' + ')} − ${T.eff[T.eff.length - 1]}`,   // on mélange les signes
        ];
        leurres = [
            randomChoice(longs),
            ...shuffle([
                sans,                                                  // on oublie une catégorie
                `${mx} × ${T.eff.length}`,                             // le plus grand effectif multiplié par le nombre de catégories
                `${mx} − ${mn}`,                                       // différence entre le plus grand et le plus petit
            ]).slice(0, 2),
        ];
        texte = `<p>${T.ctx.intro(T.groupe)} Voici les résultats, mais le total a été effacé.</p>${htmlTableau(T, 'total')}<p>Quel calcul donne le nombre de personnes interrogées en tout ?</p>`;
    }
    const options = shuffle([bonne, ...leurres]);
    const payload = makeQcm(options, bonne, texte, opts(L_CALCUL));
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: cas };
    payload._meta.params = { cas, ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i, signature: signature(T, cas === 'total' ? 'total' : i) };
    // Catégorie effacée : soustraction à l'envers, somme des connus seule, total moins un seul connu (les autres calculs restent sans code).
    if (cas === 'manquant') {
        const connus = T.eff.filter((_, j) => j !== i); const somme = connus.join(' + ');
        const nommes = [[`(${somme}) − ${T.total}`, 'effectif_manquant_a_l_envers'], [somme, 'somme_connus'], ...connus.map((c) => [`${T.total} − ${c}`, 'moins_un_seul'])];
        const erreurs = nommes.filter(([valeur]) => options.includes(valeur) && valeur !== bonne).map(([valeur, code]) => ({ code, valeur }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    }
    return payload;
};
const genCalculQcm = () => antiRepeat(construireCalculQcm, refC);

// ─── Carte 4 : tableau à double entrée (saisie) ────────────────────────────

// Revue 2026-09-07 (A7) : la question est rédigée par situation, avec les mots de la case visée
// (« Combien de garçons ont répondu non ? ») au lieu de « Combien de « Garçons » sont dans la colonne « Non » ? ».
const DOUBLES = [
    { question: 'On a demandé à des élèves s\'ils pratiquent un sport en club.', lignes: ['Filles', 'Garçons'], colonnes: ['Oui', 'Non'],
      demande: (li, co) => `Combien de ${li === 0 ? 'filles' : 'garçons'} ont répondu ${co === 0 ? 'oui' : 'non'} ?` },
    { question: 'On a demandé à des élèves s\'ils mangent à la cantine.', lignes: ['Filles', 'Garçons'], colonnes: ['Oui', 'Non'],
      demande: (li, co) => `Combien de ${li === 0 ? 'filles' : 'garçons'} ${co === 0 ? 'mangent' : 'ne mangent pas'} à la cantine ?` },
    { question: 'On a demandé à des élèves de 6e et de 5e s\'ils viennent à vélo.', lignes: ['6e', '5e'], colonnes: ['À vélo', 'Autrement'],
      demande: (li, co) => `Combien d'élèves de ${li === 0 ? '6e' : '5e'} viennent ${co === 0 ? 'à vélo' : 'autrement qu\'à vélo'} ?` },
    { question: 'On a compté les livres et les BD empruntés au CDI, neufs ou anciens.', lignes: ['Livres', 'BD'], colonnes: ['Neufs', 'Anciens'],
      demande: (li, co) => `Combien de ${li === 0 ? 'livres' : 'BD'} ${co === 0 ? (li === 0 ? 'neufs' : 'neuves') : (li === 0 ? 'anciens' : 'anciennes')} ont été ${li === 0 ? 'empruntés' : 'empruntées'} ?` },
];

const construireDouble = () => {
    for (let essai = 0; essai < 200; essai++) {
        const D = randomChoice(DOUBLES);
        const c = [[randInt(3, 15), randInt(3, 15)], [randInt(3, 15), randInt(3, 15)]];
        const lt = c.map((r) => r[0] + r[1]); const ct = [c[0][0] + c[1][0], c[0][1] + c[1][1]]; const total = lt[0] + lt[1];
        if (total > 60 || new Set(c.flat()).size !== 4) continue;
        const li = randInt(0, 1); const co = randInt(0, 1);
        const cell = (r, k) => (r === li && k === co ? MANQUE : String(c[r][k]));
        const table = tableEnonce(
            ligneTableEntete(['', ...D.colonnes, 'Total']),
            ligneTable(D.lignes[0], [cell(0, 0), cell(0, 1), String(lt[0])]),
            ligneTable(D.lignes[1], [cell(1, 0), cell(1, 1), String(lt[1])]),
            ligneTable('Total', [String(ct[0]), String(ct[1]), String(total)]),
        );
        const texte = `<p>${D.question} Voici les résultats, mais une case a été effacée.</p>${table}<p>${D.demande(li, co)}</p>`;
        const payload = makeSaisie(String(c[li][co]), texte, opts(L_DOUBLE));
        payload.contenu = { ...payload.contenu, sousType: 'double' };
        payload._meta.params = { cellules: c, li, co, signature: `${D.lignes[0]}-${c.flat().join('x')}-${li}${co}` };
        return payload;
    }
    throw new Error('gen_1126 : double entrée introuvable');
};
const genDouble = () => antiRepeat(construireDouble, refD);

// ─── Carte 5 : le tableau, vrai ou faux ? ──────────────────────────────────

let dernieresLignesVf1126 = [];

const genTableauVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const T = tirerTableau(); const i = randInt(0, T.cats.length - 1);
        const m = T.eff[i]; const connus = T.eff.filter((_, j) => j !== i); const S = connus.reduce((a, b) => a + b, 0);
        const somme = connus.join(' + ');
        const maxEff = Math.max(...T.eff); const iMax = T.eff.indexOf(maxEff);
        const autre = randomChoice(T.cats.filter((_, j) => j !== iMax));
        const candidates = [
            { l: `L'effectif manquant est ${m}.`, v: true, f: 'manquant' },
            { l: `L'effectif manquant est ${randomChoice([S, T.total - randomChoice(connus), T.total].filter((v) => v !== m))}.`, v: false, f: 'manquant' },
            { l: `L'effectif manquant se calcule par ${T.total} − (${somme}).`, v: true, f: 'calcul' },
            { l: `L'effectif manquant se calcule par ${T.total} − ${randomChoice(connus)}.`, v: false, f: 'calcul' },
            // Revue 2026-09-07 (A2) : « En tout, N personnes » se lisait dans la case Total ; la somme des
            // effectifs connus est la première étape du calcul et ne se lit pas.
            { l: `Les catégories dont on connaît l'effectif totalisent ${S} personnes.`, v: true, f: 'connus' },
            { l: `Les catégories dont on connaît l'effectif totalisent ${randomChoice([S + randomChoice([-3, -2, -1, 1, 2, 3]), T.total].filter((v) => v !== S && v > 0))} personnes.`, v: false, f: 'connus' },
            { l: `La catégorie « ${T.cats[iMax]} » est la plus choisie.`, v: true, f: 'max' },
            { l: `La catégorie « ${autre} » est la plus choisie.`, v: false, f: 'max' },
        ];
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4 || new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesVf1126 = [];
        if (lignes.some((it) => dernieresLignesVf1126.includes(it.l))) continue;
        dernieresLignesVf1126 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>${T.ctx.intro(T.groupe)} Voici les résultats, mais un effectif a été effacé.</p>${htmlTableau(T, i)}<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { ctx: T.ctx.id, cats: T.cats, eff: T.eff, total: T.total, i };
        // L'effectif manquant faux est nommé par sa valeur ; « se calcule par T − x » ne retire qu'un seul connu.
        const parLigne = new Map([
            ...erreursSures(erreursManquant(T, i), m).map((e) => [`L'effectif manquant est ${e.v}.`, e.code]),
            ...connus.map((c) => [`L'effectif manquant se calcule par ${T.total} − ${c}.`, 'moins_un_seul']),
        ]);
        const erreurs = lignes.filter((it) => !it.v && parLigne.has(it.l)).map((it) => ({ code: parLigne.get(it.l), ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1126 : V/F tableau sans tirage après 160 essais');
};

// ─── Carte 6 : effectifs et total, vrai ou faux ? (pur rappel) ─────────────

const POOL_RAPPEL = [
    { l: 'Dans un tableau d\'effectifs, le total est la somme de tous les effectifs.', v: true, f: 'total' },
    { l: 'Dans un tableau d\'effectifs, le total est le plus grand des effectifs.', v: false, f: 'total', code: 'total_pris_pour_le_maximum' },
    { l: 'Pour trouver un effectif manquant, on soustrait au total la somme des autres effectifs.', v: true, f: 'manquant' },
    { l: 'Pour trouver un effectif manquant, on additionne tous les effectifs connus.', v: false, f: 'manquant', code: 'somme_connus' },
    { l: 'Pour trouver un effectif manquant, on soustrait au total un seul des autres effectifs.', v: false, f: 'manquant', code: 'moins_un_seul' },
    { l: 'Si c\'est le total qui manque, on l\'obtient en additionnant tous les effectifs.', v: true, f: 'total_manquant' },
    { l: 'Si c\'est le total qui manque, on l\'obtient en soustrayant le plus petit effectif au plus grand.', v: false, f: 'total_manquant' },
    { l: 'Un effectif est un nombre de personnes ou d\'objets, pas la réponse qu\'ils ont donnée.', v: true, f: 'nature' },
    { l: 'Un effectif peut être un mot, comme « bleu » ou « football ».', v: false, f: 'nature' },
    { l: 'Un effectif manquant peut être égal à 0.', v: true, f: 'zero' },
    { l: 'Un effectif manquant est toujours plus grand que les autres effectifs.', v: false, f: 'zero' },
    { l: 'Deux catégories peuvent avoir le même effectif.', v: true, f: 'egaux' },
    { l: 'Deux catégories ont toujours des effectifs différents.', v: false, f: 'egaux' },
    { l: 'Un effectif manquant ne peut pas dépasser le total.', v: true, f: 'borne' },
    { l: 'Un effectif manquant peut être plus grand que le total.', v: false, f: 'borne' },
];

let dernieresLignesRappel1126 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1126 = [];
        if (lignes.some((it) => dernieresLignesRappel1126.includes(it.l))) continue;
        dernieresLignesRappel1126 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les tableaux d\'effectifs, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1126 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    manquant_saisie: genManquantSaisie,
    manquant_qcm_unique: genManquantQcm,
    calcul_qcm_unique: genCalculQcm,
    double_saisie: genDouble,
    vf_tableau_vf: genTableauVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'manquant', label: L_MANQUANT, formatsList: buildFormats('manquant') },
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'double', label: L_DOUBLE, formatsList: buildFormats('double') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    manquant_qcm_unique: ['total_pris_pour_le_maximum'],
    rappel_tableau_vf: ['total_pris_pour_le_maximum'],
    // relecture du 04/10/2026 (juge du 03/10) : « l'effectif manquant est le total », le nom dit l'inverse
    vf_tableau_vf: ['total_pris_pour_le_maximum'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
